import type { ModuleDescriptorV2 } from '../v2/contracts/public-types.ts';
import rawRules from './module-sizing.rules.json';

export type SizingMode = 'fixed' | 'content' | 'adaptive' | 'composite';

export interface ResolvedModuleSizing {
  ruleId: string;
  mode: SizingMode;
  minWidth: number;
  minHeight: number;
  idealWidth: number;
  idealHeight: number;
  maxWidth: number;
  maxHeight: number;
  preferredAspectRatio: number;
  compactWidth: number;
  compactHeight: number;
  expandedWidth: number;
  minAspectRatio?: number;
  maxAspectRatio?: number;
  labelWidth?: number;
  helpWidth?: number;
}

export interface ModulePresentationSizing {
  /** ModuleHelp reserves a separate right-side slot only around controls. */
  help?: string;
  control?: boolean;
}

interface RawProfile {
  mode: Exclude<SizingMode, 'composite'>;
  min: [number, number];
  ideal: [number, number];
  max: [number, number];
  aspect: number;
  aspectRange?: [number, number];
  density?: { compact: [number, number]; expanded: number };
}

interface RawRules {
  version: number;
  referencePage: { width: number; height: number; gap: number; safeFillRatio: number };
  profiles: Record<string, RawProfile>;
  modules: Record<string, string>;
  composite: {
    minItems: number;
    maxItems: number;
    sameModuleKey: boolean;
    gap: number;
    padding: number;
    targetAspect: number;
    columnCandidates: Record<string, number[]>;
  };
}

const rules = rawRules as unknown as RawRules;

function finitePositive(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) && (value ?? 0) > 0 ? value! : fallback;
}

export function moduleSizingRules(): Readonly<RawRules> {
  return rules;
}

export function resolveModuleSizing(
  moduleKey: string,
  fallback?: ModuleDescriptorV2['layoutHints'],
  config?: unknown,
  presentation?: ModulePresentationSizing,
): ResolvedModuleSizing {
  const declaredRuleId = rules.modules[moduleKey]
    ?? (moduleKey.startsWith('probfun.prekernel-control-') ? 'run-control' : undefined);
  const ruleId = declaredRuleId ?? 'default';
  const profile = rules.profiles[ruleId] ?? rules.profiles.default!;
  // Registered modules take all sizing authority from the declarative rule
  // file. Descriptor hints are a compatibility fallback only for an unknown
  // migrated module, otherwise two size authorities would drift again.
  const hints = declaredRuleId ? undefined : fallback;
  const minWidth = finitePositive(hints?.minWidth, profile.min[0]);
  const minHeight = finitePositive(hints?.minHeight, profile.min[1]);
  const maxWidth = Math.max(minWidth, finitePositive(hints?.maxWidth, profile.max[0]));
  const maxHeight = Math.max(minHeight, finitePositive(hints?.maxHeight, profile.max[1]));
  const sizing: ResolvedModuleSizing = {
    ruleId,
    mode: hints?.sizingMode ?? profile.mode,
    minWidth,
    minHeight,
    idealWidth: Math.min(maxWidth, Math.max(minWidth, finitePositive(hints?.idealWidth, profile.ideal[0]))),
    idealHeight: Math.min(maxHeight, Math.max(minHeight, finitePositive(hints?.idealHeight, profile.ideal[1]))),
    maxWidth,
    maxHeight,
    preferredAspectRatio: finitePositive(hints?.preferredAspectRatio, profile.aspect),
    compactWidth: finitePositive(profile.density?.compact[0], Math.max(minWidth + 1, Math.round(profile.ideal[0] * 0.72))),
    compactHeight: finitePositive(profile.density?.compact[1], Math.max(minHeight + 1, Math.round(profile.ideal[1] * 0.72))),
    expandedWidth: finitePositive(profile.density?.expanded, Math.max(minWidth + 2, Math.round(profile.ideal[0] * 1.35))),
    minAspectRatio: profile.aspectRange?.[0],
    maxAspectRatio: profile.aspectRange?.[1],
  };
  if (moduleKey === 'probfun.markdown-latex@3')
    return sizeMarkdownContent(sizing, config);
  if (moduleKey === 'probfun.button@2' && config && typeof config === 'object' && 'label' in config && typeof config.label === 'string') {
    sizing.labelWidth = textWidth(config.label, 7, 14);
    const naturalWidth = textWidth(config.label, 7, 14) + 32;
    // Keep short action labels on one line, but cap the minimum so long labels
    // can wrap into a compact button instead of forcing a wide empty column.
    // The ideal width still follows the natural label width when there is room.
    // Buttons may wrap a long label (including the help slot) so a sizeable
    // action palette can use three columns instead of forcing seven sparse
    // rows. The renderer keeps the label readable and raises the cell height
    // when it needs up to four lines.
    const readableMinimum = Math.min(naturalWidth, 80);
    sizing.minWidth = Math.max(sizing.minWidth, Math.min(sizing.maxWidth, readableMinimum));
    sizing.idealWidth = Math.max(sizing.idealWidth, sizing.minWidth);
  }
  if (moduleKey === 'probfun.string-control@3' && config && typeof config === 'object' && 'label' in config && typeof config.label === 'string')
    sizing.labelWidth = textWidth(config.label, 7, 14);
  if (moduleKey === 'probfun.boolean-control@3' && config && typeof config === 'object' && 'label' in config && typeof config.label === 'string') {
    // Label + 38px switch + 10px gap + 24px padding, before help chrome.
    sizing.minWidth = Math.max(sizing.minWidth, Math.min(sizing.maxWidth, textWidth(config.label, 7, 14) + 72));
    sizing.idealWidth = Math.max(sizing.idealWidth, sizing.minWidth);
  }
  if (presentation?.control && presentation.help?.trim()) {
    // Match ModuleHelp's 28px control chrome without polluting component config.
    const helpWidth = 28;
    sizing.helpWidth = helpWidth;
    for (const key of ['minWidth', 'idealWidth', 'maxWidth', 'compactWidth', 'expandedWidth'] as const)
      sizing[key] += helpWidth;
  }
  return sizing;
}

/**
 * Content-sized text stays bounded by the shared rule and scrolls if live
 * values grow. This estimate controls placement, never scientific state.
 */
function sizeMarkdownContent(sizing: ResolvedModuleSizing, config: unknown): ResolvedModuleSizing {
  if (!config || typeof config !== 'object' || Array.isArray(config))
    return sizing;
  const value = config as { title?: unknown; content?: unknown };
  if (typeof value.content !== 'string')
    return sizing;
  // A standalone interpolation may contain an entire live explanation, not a
  // short number. Its key gives no reliable information about the rendered
  // length. Preserve a readable text panel; labelled numeric summaries below
  // still benefit from content-based compact sizing.
  if (value.content.split('\n').some(line => /^\s*(?:\{\{[^}]+\}\}\s*)+$/.test(line))) {
    return {
      ...sizing,
      minWidth: Math.max(sizing.minWidth, 260),
      // A live interpolation may eventually expand, but its first render is
      // often one short sentence. Reserve a compact readable card and let the
      // module's own overflow handle a later long value instead of baking a
      // 180px blank panel into every candidate.
      minHeight: Math.max(sizing.minHeight, 96),
      idealHeight: Math.min(Math.max(120, sizing.idealHeight), 144),
      idealWidth: Math.max(sizing.idealWidth, 420),
    };
  }
  const source = `${typeof value.title === 'string' ? `## ${value.title}\n` : ''}${value.content}`
    .trim()
    .replace(/\{\{[^}]+\}\}/g, '000000000000');
  const compactDynamic = /\{\{[^}]+\}\}/.test(value.content)
    && value.content.split('\n').length <= 2
    && value.content.length < 500;
  const metrics: Array<{ width: number; lineHeight: number }> = [];
  let displayMath = false;
  let formulaWidth = 0;
  for (const rawLine of source.split('\n')) {
    const line = rawLine.trim();
    if (line === '$$' || line === '\\[' || line === '\\]') {
      if (displayMath && formulaWidth > 0)
        metrics.push({ width: formulaWidth, lineHeight: 30 });
      displayMath = line === '$$' ? !displayMath : line === '\\[';
      formulaWidth = 0;
      continue;
    }
    const inlineDisplay = /^\$\$.*\$\$$/.test(line) || /^\\\[.*\\\]$/.test(line);
    if (displayMath || inlineDisplay) {
      formulaWidth = Math.max(formulaWidth, textWidth(line.replace(/^\$\$|\$\$$|^\\\[|\\\]$/g, ''), 8, 15));
      if (inlineDisplay) {
        metrics.push({ width: formulaWidth, lineHeight: 30 });
        formulaWidth = 0;
      }
      continue;
    }
    if (!line)
      continue;
    const heading = /^#{1,6}\s/.test(line);
    const plain = line.replace(/^#{1,6}\s+/, '').replace(/\*\*|__|`/g, '');
    metrics.push({ width: textWidth(plain, heading ? 8 : 7, heading ? 16 : 14), lineHeight: heading ? 29 : 24 });
  }
  if (displayMath && formulaWidth > 0)
    metrics.push({ width: formulaWidth, lineHeight: 30 });
  const naturalWidth = Math.max(...metrics.map(line => line.width), 0) + 28;
  const longText = metrics.length >= 4 || naturalWidth > sizing.idealWidth * 2;
  const minWidth = longText ? Math.max(sizing.minWidth, 260) : Math.max(96, Math.min(sizing.minWidth, naturalWidth));
  const maxWidth = sizing.maxWidth;
  const idealWidth = Math.max(minWidth, Math.min(sizing.idealWidth, maxWidth, naturalWidth));
  const heightAt = (width: number) => {
    const contentWidth = Math.max(1, width - 28);
    return 24 + metrics.reduce((sum, line, index) => (
      sum + (index === 0 ? 0 : 8) + Math.max(1, Math.ceil(line.width / contentWidth)) * line.lineHeight
    ), 0);
  };
  const maxHeight = Math.min(sizing.maxHeight, Math.max(39, heightAt(minWidth)));
  const minHeight = longText ? Math.max(sizing.minHeight, Math.min(180, heightAt(idealWidth))) : Math.min(sizing.minHeight, Math.max(39, heightAt(idealWidth)));
  if (compactDynamic) {
    const compactHeight = 84;
    return {
      ...sizing,
      minWidth,
      minHeight: Math.min(minHeight, 84),
      idealWidth,
      idealHeight: compactHeight,
      maxWidth,
      maxHeight: Math.max(compactHeight, maxHeight),
      compactWidth: Math.min(sizing.compactWidth, maxWidth),
      compactHeight,
      expandedWidth: maxWidth,
    };
  }
  return {
    ...sizing,
    minWidth,
    minHeight,
    idealWidth,
    idealHeight: Math.max(minHeight, Math.min(maxHeight, heightAt(idealWidth))),
    maxWidth,
    maxHeight,
    compactWidth: Math.min(sizing.compactWidth, maxWidth),
    compactHeight: Math.min(sizing.compactHeight, maxHeight),
    expandedWidth: maxWidth,
  };
}

function textWidth(value: string, asciiWidth: number, wideWidth: number): number {
  return [...value].reduce((sum, char) => sum + (char.codePointAt(0)! > 255 ? wideWidth : asciiWidth), 0);
}

export function compositeColumnCandidates(count: number): number[] {
  const configured = rules.composite.columnCandidates[String(count)] ?? [];
  const generated = Array.from({ length: Math.min(count, 12) }, (_, index) => index + 1);
  const divisors = Array.from({ length: count }, (_, index) => index + 1).filter(columns => count % columns === 0);
  return [...new Set([...configured, ...generated, ...divisors].filter(columns => columns >= 1 && columns <= count))];
}

export function resolveCompositeSizing(
  children: readonly ResolvedModuleSizing[],
): ResolvedModuleSizing {
  const count = children.length;
  if (count < rules.composite.minItems || count > rules.composite.maxItems)
    throw new Error(`组合模块只支持 ${rules.composite.minItems}–${rules.composite.maxItems} 个子模块`);
  const columns = compositeColumnCandidates(count);
  const gap = rules.composite.gap;
  const choices = columns.map((columnCount) => {
    const rowCount = Math.ceil(count / columnCount);
    const width = (key: 'minWidth' | 'idealWidth' | 'maxWidth') => Array.from({ length: columnCount }, (_, column) =>
      Math.max(...children.filter((_, index) => index % columnCount === column).map(child => child[key]))).reduce((sum, current) => sum + current, 0) + (columnCount - 1) * gap;
    const height = (key: 'minHeight' | 'idealHeight' | 'maxHeight') => Array.from({ length: rowCount }, (_, row) =>
      Math.max(...children.slice(row * columnCount, (row + 1) * columnCount).map(child => child[key]))).reduce((sum, current) => sum + current, 0) + (rowCount - 1) * gap;
    const dimensions = {
      columns: columnCount,
      rows: rowCount,
      minWidth: width('minWidth'),
      minHeight: height('minHeight'),
      idealWidth: width('idealWidth'),
      idealHeight: height('idealHeight'),
      maxWidth: width('maxWidth'),
      maxHeight: height('maxHeight'),
    };
    const aspect = dimensions.idealWidth / dimensions.idealHeight;
    return { ...dimensions, aspectError: Math.abs(aspect - rules.composite.targetAspect) };
  });
  const contentGroup = children.every(child => child.mode === 'content');
  choices.sort((left, right) => contentGroup
    ? left.idealHeight - right.idealHeight || left.aspectError - right.aspectError
    : left.aspectError - right.aspectError || left.idealWidth * left.idealHeight - right.idealWidth * right.idealHeight);
  const selected = choices[0]!;
  return {
    ruleId: `composite:${count}`,
    mode: 'composite',
    minWidth: selected.minWidth,
    minHeight: selected.minHeight,
    idealWidth: selected.idealWidth,
    idealHeight: selected.idealHeight,
    maxWidth: selected.maxWidth,
    maxHeight: selected.maxHeight,
    preferredAspectRatio: selected.idealWidth / selected.idealHeight,
    compactWidth: selected.minWidth + 1,
    compactHeight: selected.minHeight + 1,
    expandedWidth: selected.maxWidth,
  };
}

export function estimatedPageFill(sizing: ResolvedModuleSizing): number {
  const page = rules.referencePage;
  return Math.min(1, (sizing.idealWidth * sizing.idealHeight) / (page.width * page.height * page.safeFillRatio));
}
